import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOrganizationStore } from "@/stores/organization";
import { useUserStore } from "@/stores/user";
import AvatarPopover from "./avatar-popover";
import { getInitials, getRandomOrgAvatar } from "@/utils/utils";
import { Label } from "./ui/label";
import { DEFAULT_ORG_AVATAR } from "@/lib/constants";
import { useRouter } from "next/navigation";
import { startNavigationFeedback } from "@/lib/navigation-feedback";
import { getOrganizationJoinRequestStatus } from "@/services/member.client";
import { LoaderCircle, SearchX } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import type { Organization } from "@/types/organization";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

const DIALOG_TITLE = "Set up your organization";
const CREATE_DIALOG_TITLE = "Create organization";
const JOIN_DIALOG_TITLE = "Join organization";
const PLACEHOLDER_TEXT = "Libum Credit Union";

type OrganizationDialogView = "selection" | "create" | "join";
type OrganizationDialogDirection = "initial" | "forward" | "back";
type JoinPrecheckStatus = "idle" | "checking" | "valid" | "invalid";
type JoinPrecheckSnapshot = {
  status: Exclude<JoinPrecheckStatus, "idle">;
  organization: Organization | null;
  requestStatus:
    | "active"
    | "invited"
    | "declined"
    | "requested"
    | "rejected"
    | null;
  error: string | null;
};

function formatOrganizationAge(createdAt?: string) {
  if (!createdAt) return null;

  const createdTime = new Date(createdAt).getTime();
  if (Number.isNaN(createdTime)) return null;

  const elapsedMs = Date.now() - createdTime;
  const elapsedDays = Math.max(0, Math.floor(elapsedMs / 86400000));

  if (elapsedDays < 1) return "Created today";
  if (elapsedDays < 30) {
    return `Created ${elapsedDays} ${elapsedDays === 1 ? "day" : "days"} ago`;
  }

  const elapsedMonths = Math.floor(elapsedDays / 30);
  if (elapsedMonths < 12) {
    return `Created ${elapsedMonths} ${elapsedMonths === 1 ? "month" : "months"} ago`;
  }

  const elapsedYears = Math.floor(elapsedMonths / 12);
  return `Created ${elapsedYears} ${elapsedYears === 1 ? "year" : "years"} ago`;
}

function formatMemberCount(memberCount?: number) {
  if (memberCount === undefined) return null;

  return `${memberCount} ${memberCount === 1 ? "member" : "members"}`;
}

interface OrganizationCreateDialogProps {
  controlledOpen?: boolean;
  initialView?: OrganizationDialogView;
  isForced?: boolean;
  onSuccess?: (organizationId: string) => void;
  onJoinRequestSuccess?: (organizationId: string) => void;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export default function OrganizationCreateDialog({
  controlledOpen,
  initialView,
  isForced,
  onSuccess,
  onJoinRequestSuccess,
  onOpenChange,
  trigger,
}: OrganizationCreateDialogProps) {
  const router = useRouter();
  const { user, organizations, setOrganizations, setUser } = useUserStore();
  const {
    createOrganization,
    createOrganizationMember,
    fetchOrganization,
    setPendingJoinRequest,
  } = useOrganizationStore();
  const getInitialView = useCallback(
    () => initialView ?? (isForced ? "selection" : "create"),
    [initialView, isForced],
  );
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(getRandomOrgAvatar());
  const [joinOrganizationId, setJoinOrganizationId] = useState("");
  const [view, setView] = useState<OrganizationDialogView>(getInitialView);
  const [previousView, setPreviousView] =
    useState<OrganizationDialogView>(getInitialView);
  const [viewDirection, setViewDirection] =
    useState<OrganizationDialogDirection>("initial");
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [joinPrecheckStatus, setJoinPrecheckStatus] =
    useState<JoinPrecheckStatus>("idle");
  const [joinPrecheckError, setJoinPrecheckError] = useState<string | null>(
    null,
  );
  const [checkedOrganization, setCheckedOrganization] =
    useState<Organization | null>(null);
  const [checkedJoinRequestStatus, setCheckedJoinRequestStatus] = useState<
    "active" | "invited" | "declined" | "requested" | "rejected" | null
  >(null);
  const [renderedJoinPrecheck, setRenderedJoinPrecheck] =
    useState<JoinPrecheckSnapshot | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const joinPrecheckIdRef = useRef(0);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      const resetTimeout = window.setTimeout(() => {
        const resetView = getInitialView();
        setName("");
        setJoinOrganizationId("");
        setView(resetView);
        setPreviousView(resetView);
        setViewDirection("initial");
        setAvatar(getRandomOrgAvatar());
        setIsSubmitted(false);
        setJoinPrecheckStatus("idle");
        setJoinPrecheckError(null);
        setCheckedOrganization(null);
        setCheckedJoinRequestStatus(null);
        setRenderedJoinPrecheck(null);
      }, 200);

      return () => window.clearTimeout(resetTimeout);
    }
  }, [getInitialView, isOpen]);

  const handleSubmit = useCallback(async () => {
    if (isSubmitted || view === "selection") return;

    try {
      setIsSubmitted(true);

      if (view === "join") {
        if (!user) {
          setIsSubmitted(false);
          return;
        }

        const requestedOrganizationId = joinOrganizationId.trim();

        if (
          !checkedOrganization ||
          checkedOrganization.id !== requestedOrganizationId ||
          checkedJoinRequestStatus === "active" ||
          checkedJoinRequestStatus === "requested" ||
          checkedJoinRequestStatus === "invited"
        ) {
          setIsSubmitted(false);
          return;
        }

        const member = await createOrganizationMember(
          user.id,
          requestedOrganizationId,
          user.id,
          "UNASSIGNED",
          "member",
          "requested",
        );

        setPendingJoinRequest({
          ...checkedOrganization,
          logo: checkedOrganization.logo || DEFAULT_ORG_AVATAR,
          joinRequestId: member.id,
        });

        setIsSubmitted(false);
        onJoinRequestSuccess?.(requestedOrganizationId);
        setIsOpen(false);
        startNavigationFeedback();
        router.push("/settings/organizations");
        return;
      }

      const organization = await createOrganization(name.trim(), avatar);

      if (user && organization) {
        setUser({
          ...user,
          organizations: Array.from(
            new Set([...user.organizations, organization.id]),
          ),
        });
        setOrganizations([
          ...organizations.filter((org) => org.id !== organization.id),
          organization,
        ]);
        await fetchOrganization(organization.id, true, true);
      }

      setIsOpen(false);

      if (onSuccess) {
        onSuccess(organization.id);
        setName("");
        setIsSubmitted(false);
      }
    } catch (error: any) {
      console.warn(error.message);
      setIsSubmitted(false);
    }
  }, [
    avatar,
    checkedJoinRequestStatus,
    checkedOrganization,
    isSubmitted,
    joinOrganizationId,
    name,
    createOrganization,
    createOrganizationMember,
    fetchOrganization,
    onJoinRequestSuccess,
    onSuccess,
    router,
    setIsOpen,
    setPendingJoinRequest,
    setOrganizations,
    setUser,
    organizations,
    user,
    view,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setName(newValue);
  };

  const handleAvatarChange = (src: string) => {
    setAvatar(src);
  };

  const handleJoinOrganizationIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setJoinOrganizationId(e.target.value);
    setJoinPrecheckStatus("idle");
    setJoinPrecheckError(null);
    setCheckedOrganization(null);
    setCheckedJoinRequestStatus(null);
  };

  const handleViewChange = (nextView: OrganizationDialogView) => {
    if (nextView === view) return;

    setPreviousView(view);
    setViewDirection(nextView === "selection" ? "back" : "forward");
    setView(nextView);
  };

  useEffect(() => {
    let frameId: number;

    if (isOpen && view !== "selection") {
      frameId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isOpen, view]);

  useEffect(() => {
    const requestedOrganizationId = joinOrganizationId.trim();

    if (!isOpen || view !== "join" || !requestedOrganizationId) {
      setJoinPrecheckStatus("idle");
      setJoinPrecheckError(null);
      setCheckedOrganization(null);
      setCheckedJoinRequestStatus(null);
      return;
    }

    const precheckId = joinPrecheckIdRef.current + 1;
    joinPrecheckIdRef.current = precheckId;
    setJoinPrecheckStatus("checking");
    setJoinPrecheckError(null);
    setCheckedOrganization(null);
    setCheckedJoinRequestStatus(null);

    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await getOrganizationJoinRequestStatus(
          requestedOrganizationId,
        );

        if (joinPrecheckIdRef.current !== precheckId) return;
        if (!result.organization) {
          setJoinPrecheckStatus("invalid");
          setJoinPrecheckError("Organization not found");
          return;
        }

        setCheckedOrganization(result.organization);
        setCheckedJoinRequestStatus(result.request?.status ?? null);
        setJoinPrecheckStatus("valid");
      } catch (error: any) {
        if (joinPrecheckIdRef.current !== precheckId) return;

        setJoinPrecheckStatus("invalid");
        setJoinPrecheckError(error.message || "Organization not found");
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, joinOrganizationId, view]);

  useEffect(() => {
    if (joinPrecheckStatus === "checking") {
      setRenderedJoinPrecheck({
        status: "checking",
        organization: null,
        requestStatus: null,
        error: null,
      });
      return;
    }

    if (joinPrecheckStatus === "valid" && checkedOrganization) {
      setRenderedJoinPrecheck({
        status: "valid",
        organization: checkedOrganization,
        requestStatus: checkedJoinRequestStatus,
        error: null,
      });
      return;
    }

    if (joinPrecheckStatus === "invalid" && joinPrecheckError) {
      setRenderedJoinPrecheck({
        status: "invalid",
        organization: null,
        requestStatus: null,
        error: joinPrecheckError,
      });
    }
  }, [
    checkedJoinRequestStatus,
    checkedOrganization,
    joinPrecheckError,
    joinPrecheckStatus,
  ]);

  const viewAnimationClass =
    view === previousView
      ? "duration-300 animate-in fade-in-75 zoom-in-98 ease-out"
      : viewDirection === "back"
        ? "duration-300 animate-in fade-in-75 slide-in-from-left-4 ease-out"
        : "duration-300 animate-in fade-in-75 slide-in-from-right-4 ease-out";

  const dialogTitle =
    view === "selection"
      ? DIALOG_TITLE
      : view === "join"
        ? JOIN_DIALOG_TITLE
        : CREATE_DIALOG_TITLE;

  const dialogDescription =
    view === "selection"
      ? "Create a workspace or request access with an organization ID."
      : view === "join"
        ? "Enter the organization ID provided by an owner or admin."
        : isForced
          ? "Name your workspace and choose an organization avatar."
          : undefined;

  const submitLabel =
    view === "join"
      ? "Request to join"
      : `${isForced ? "Get started" : "Create organization"}`;
  const canReturnToSelection = isForced || initialView === "selection";

  const isJoinRequestBlocked =
    checkedJoinRequestStatus === "active" ||
    checkedJoinRequestStatus === "requested" ||
    checkedJoinRequestStatus === "invited";

  const getJoinRequestBlockMessage = (
    requestStatus:
      | "active"
      | "invited"
      | "declined"
      | "requested"
      | "rejected"
      | null,
  ) =>
    requestStatus === "active"
      ? "You already belong to this organization."
      : requestStatus === "requested"
        ? "You've already requested access to this organization."
        : requestStatus === "invited"
          ? "You already have an invitation to this organization."
          : null;

  const isSubmitDisabled =
    view === "create"
      ? !name.trim()
      : !joinOrganizationId.trim() ||
        joinPrecheckStatus !== "valid" ||
        !checkedOrganization ||
        checkedOrganization.id !== joinOrganizationId.trim() ||
        isJoinRequestBlocked;

  const shouldShowJoinPrecheck =
    joinPrecheckStatus === "checking" ||
    joinPrecheckStatus === "invalid" ||
    (joinPrecheckStatus === "valid" && !!checkedOrganization);

  const renderedJoinRequestBlockMessage = getJoinRequestBlockMessage(
    renderedJoinPrecheck?.requestStatus ?? null,
  );
  const renderedOrganizationAge = formatOrganizationAge(
    renderedJoinPrecheck?.organization?.createdAt,
  );
  const renderedMemberCount = formatMemberCount(
    renderedJoinPrecheck?.organization?.memberCount,
  );
  const renderedOrganizationMetadata = [
    renderedOrganizationAge,
    renderedMemberCount,
  ].filter(Boolean);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        className="bg-card"
        onOpenAutoFocus={(e) => {
          if (isForced && view === "selection") {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => isForced === true && e.preventDefault()}
      >
        <div
          key={view}
          className={cn(
            "grid gap-4 transition-[gap] duration-300 ease-out",
            viewAnimationClass,
          )}
        >
          <DialogHeader className="flex">
            <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
              {dialogTitle}
            </DialogTitle>
            <DialogDescription className="text-center">
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>
          {view === "selection" ? (
            <Tabs
              value=""
              onValueChange={(value) =>
                handleViewChange(value as OrganizationDialogView)
              }
              className="w-full"
            >
              <TabsList className="flex h-full select-none flex-col bg-cardTabs sm:flex-row">
                <TabsTrigger
                  value="create"
                  className="flex h-full flex-col py-3 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                  onClick={() => handleViewChange("create")}
                >
                  <span className="font-semibold">Create</span>
                  <div className="flex py-3">
                    <Image
                      src="/emojis/card-index-dividers.svg"
                      alt=""
                      width={36}
                      height={36}
                    />
                  </div>
                  <span className="text-xs text-muted">
                    Start a new organization
                  </span>
                </TabsTrigger>
                <div
                  aria-hidden="true"
                  className="h-[2px] w-4/5 shrink-0 bg-card sm:h-24 sm:w-[2px]"
                />
                <TabsTrigger
                  value="join"
                  className="relative flex h-full flex-col py-3 transition-colors hover:!bg-card hover:text-primary-foreground data-[state=active]:!bg-card"
                  onClick={() => handleViewChange("join")}
                >
                  <span className="font-semibold">Join</span>
                  <div className="flex py-3">
                    <Image
                      src="/emojis/locked-with-key.svg"
                      alt=""
                      width={36}
                      height={36}
                    />
                  </div>
                  <span className="text-xs text-muted">
                    Request access by ID
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : view === "join" ? (
            <div className="-mt-2 space-y-3 pb-2">
              <div
                className={cn(
                  "grid transition-[grid-template-rows] duration-300 ease-in-out",
                  shouldShowJoinPrecheck
                    ? "grid-rows-[1fr]"
                    : "grid-rows-[0fr]",
                )}
                onTransitionEnd={(e) => {
                  if (
                    e.propertyName === "grid-template-rows" &&
                    !shouldShowJoinPrecheck
                  ) {
                    setRenderedJoinPrecheck(null);
                  }
                }}
              >
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-300 ease-in-out",
                    shouldShowJoinPrecheck
                      ? "translate-y-0 opacity-100"
                      : "translate-y-2 opacity-0",
                  )}
                >
                  {renderedJoinPrecheck?.status === "checking" && (
                    <div className="mb-1 flex min-h-[116px] items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                      <LoaderCircle className="size-3.5 animate-spin" />
                      Checking organization
                    </div>
                  )}
                  {renderedJoinPrecheck?.status === "valid" &&
                    renderedJoinPrecheck.organization && (
                      <div className="flex min-h-[132px] flex-col items-center justify-center px-3 py-4 text-center">
                        <Avatar className="mx-auto mb-3 size-14 rounded-sm">
                          <AvatarImage
                            src={
                              renderedJoinPrecheck.organization.logo ||
                              DEFAULT_ORG_AVATAR
                            }
                            alt=""
                          />
                          <AvatarFallback className="rounded-sm text-sm">
                            {getInitials(
                              renderedJoinPrecheck.organization.name,
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="font-medium text-gray-1100 dark:text-gray-100">
                          {renderedJoinPrecheck.organization.name}
                        </div>
                        {renderedOrganizationMetadata.length > 0 && (
                          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            {renderedOrganizationMetadata.map(
                              (metadata, index) => (
                                <span key={metadata} className="contents">
                                  {index > 0 && (
                                    <span className="inline-flex items-center">
                                      <span className="size-0.5 translate-y-px rounded-full bg-brand-primary" />
                                    </span>
                                  )}
                                  <span>{metadata}</span>
                                </span>
                              ),
                            )}
                          </div>
                        )}
                        {renderedJoinRequestBlockMessage && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            {renderedJoinRequestBlockMessage}
                          </div>
                        )}
                      </div>
                    )}
                  {renderedJoinPrecheck?.status === "invalid" && (
                    <div className="mb-1 p-3 text-center">
                      <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-sm bg-cardTabs text-muted-foreground">
                        <SearchX className="size-5" />
                      </div>
                      <div className="font-medium text-gray-1100 dark:text-gray-100">
                        No organization found
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Check the organization ID and try again.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="join-org-id">Organization ID</Label>
                <Input
                  ref={inputRef}
                  id="join-org-id"
                  type="text"
                  value={joinOrganizationId}
                  className="mt-0"
                  onChange={handleJoinOrganizationIdChange}
                  placeholder="org_123456789"
                  autoCapitalize="none"
                  autoComplete="off"
                  autoCorrect="off"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  data-form-type="other"
                  spellCheck={false}
                />
              </div>
            </div>
          ) : view === "create" ? (
            <div className="flex items-center py-2">
              <AvatarPopover
                avatar={avatar}
                onAvatarChange={handleAvatarChange}
                type="organization"
              />
              <Input
                ref={inputRef}
                id="org-name"
                type="text"
                value={name}
                className="mt-0"
                onChange={handleInputChange}
                placeholder={PLACEHOLDER_TEXT}
              />
            </div>
          ) : null}
          {view !== "selection" && (
            <DialogFooter className="!flex-row items-center justify-end gap-2 sm:space-x-0">
              {canReturnToSelection && (
                <Button
                  type="button"
                  variant="form"
                  size="lg"
                  onClick={() => handleViewChange("selection")}
                  disabled={isSubmitted}
                >
                  Back
                </Button>
              )}
              <div
                aria-hidden={!isSubmitted}
                className={cn(
                  "grid place-items-center overflow-hidden transition-[width,opacity,transform] duration-300 ease-in-out",
                  isSubmitted
                    ? "w-4 translate-x-0 opacity-100"
                    : "w-0 -translate-x-1 opacity-0",
                )}
              >
                <LoaderCircle className="size-4 animate-spin text-brand-primary" />
              </div>
              <Button
                size="lg"
                disabled={isSubmitDisabled || isSubmitted}
                onClick={handleSubmit}
                aria-busy={isSubmitted}
                className={cn(
                  "transition-all duration-300 ease-in-out",
                  isSubmitted ? "w-full sm:w-56" : "w-full sm:w-auto",
                )}
              >
                {submitLabel}
              </Button>
            </DialogFooter>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
